import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { apiSlice } from '../apiSlice';
import { DeleteResponse } from '../schemas/apiResponse';
import { Collection } from '../schemas/collection';
import { PaginatedResponse } from '../schemas/pagination';
import { Ranch } from '../schemas/ranch';
import { filterParamsAdapter } from '../utils/filterParamsAdapter';
import { providesList } from '../utils/providesList';

type RanchFilterParams = {
  id__in?: string[];
  name__like?: string[];
  customer__id__in?: string[];
  customer__short_code__in?: string[];
};

const RANCHES_PATH = '/ranches';

const ranchEndpoints = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRanches: builder.query<Ranch[], RanchFilterParams | void>({
      query: (params) => ({
        url: RANCHES_PATH,
        params: params ? filterParamsAdapter(params) : undefined,
      }),
      transformResponse: (res: Ranch[]) =>
        res.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        ),
      providesTags: (result) => providesList(result, 'Ranch'),
    }),
    getRanch: builder.query<Ranch, string>({
      query: (id) => `${RANCHES_PATH}/${id}`,
      providesTags: (result, error, id) => [{ type: 'Ranch', id }],
    }),
    addRanch: builder.mutation<Ranch, Partial<Ranch>>({
      query: (body) => ({
        url: RANCHES_PATH,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Ranch', id: 'LIST' }],
    }),
    updateRanch: builder.mutation<Ranch, Partial<Ranch>>({
      query: (ranch) => ({
        url: `${RANCHES_PATH}/${ranch.id}`,
        method: 'PATCH',
        body: ranch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Ranch', id }],
    }),
    deleteRanch: builder.mutation<DeleteResponse, string>({
      query(id) {
        return {
          url: `${RANCHES_PATH}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Ranch', id }],
    }),
    getRanchCollections: builder.query<Collection[], Ranch['id']>({
      query: (ranchId) => `${RANCHES_PATH}/${ranchId}/collections`,
      providesTags: (result) => providesList(result, 'Collection'),
    }),
    getCollectionsForRanches: builder.query<
      Collection[],
      CollectionsForRanchesQueryArgs
    >({
      // TODO: Talk to Yuriy about this idea or just implement it on the frontend side
      // query: (ranchIds) => ({
      //   url: `${RANCHES_PATH}/collections`,
      //   params: { ranch__id__in: ranchIds },
      // }),
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const { ranchIds, ...params } = arg;
        const pageRequests = ranchIds.map((ranchId) =>
          baseQuery({
            url: `${RANCHES_PATH}/${ranchId}/collections`,
            params,
          })
        );

        const results = await Promise.all(pageRequests);

        const flattenedCollectionResults = results.flatMap(
          (result) =>
            ((result as QueryReturnValue<PaginatedResponse<Collection>>).data
              ?.items as Collection[]) ?? []
        );

        return {
          data: flattenedCollectionResults,
        };
      },
      providesTags: (result) => providesList(result, 'Collection'),
    }),
  }),
});

export const {
  useGetRanchesQuery,
  useGetRanchQuery,
  useAddRanchMutation,
  useUpdateRanchMutation,
  useDeleteRanchMutation,
  useGetRanchCollectionsQuery,
  useGetCollectionsForRanchesQuery,
} = ranchEndpoints;

export type CollectionsForRanchesQueryArgs = {
  ranchIds: Ranch['id'][];
  collection_date__gte?: string;
  collection_date__lte?: string;
};
